define((require) => {
    "use strict";

    const AbstractWidgetModel = require("shared/ui/experience/widgets/AbstractWidgetModel");
    const generate            = require("nanoid/generate");
    const Size                = require("shared/util/Size");
    const MathUtil            = require("shared/util/MathUtil").default;
    const EditorEventFilters  = require("ui/editor/EditorEventFilters").default;
    const uuidAlphabet        = "0123456789abcdefghijklmnopqrstuvwxyz-";

    /**
     * Mixes in Editor related AbstractWidgetModel fields. <p>
     *
     *
     * @class WidgetTypes
     */
    return AbstractWidgetModel.mixin({
        computed: {
            uuidSet: {
                deps: [],
                computeDefault: true,
                silent: true,
                value() {
                    const uuid = this.get("uuid");
                    const definedUUID = uuid && uuid.length > 0 ? uuid : generate(uuidAlphabet, 21);
                    this.set("uuid", definedUUID, {cause: EditorEventFilters.SUPERFICIAL});
                    return definedUUID;
                }
            }
        },

        defaults: {
            isVisible: true
        },

        transient: ["highlighted", "overlapped"],

        /**
         * Returns the largest possible size that fits inside a 100% x 100% box, while respecting
         * the aspect ratio of percentageSize. The width and/or the height of the returned size
         * will always be 100.
         * @param {Size} percentageSize Size in percentages relative to size of the scene.
         * @return {Size} Fitted size in percentages relatavive to the size of the scene.
         */
        _getFittedSize: function(percentageSize) {
            var percentageAspectRatio = percentageSize.width / percentageSize.height;
            // Shrink a 100% x 100% box to fit respecting the widget's aspect ratio.
            var fittedSize = Size.from(100, 100).shrinkToAspectRatio(percentageAspectRatio);

            return fittedSize;
        },

        /**
         * Returns self description string
         * 1) if widget has `_getSelfDescription` method defined,
         * and it returns a value, use it
         * 2) Otherwise returns Basic description
         *
         * @return {string} The self description
         */
        _getBasicDescription: function() {
            const isHasSelfDescription = typeof this._getSelfDescription === "function"
                && this._getSelfDescription();

            return isHasSelfDescription ? this._getSelfDescription()
                : `${this.get("type").charAt(0).toUpperCase()}${this.get("type").slice(1)}: `;
        },

        /**
         * Sets the rotation angle to zero degrees.
         */
        _resetRotation: function() {
            this.set({
                rotation: 0
            });
        },

        /**
         * Applies the specified size to the widget. With rounding to contain correct enough accuracy.
         * @param {Size} percentageSize Size of the widget in percentages relative to the size of the experience.
         */
        _setSize: function(percentageSize, experience) {
            this.set({
                width: MathUtil.roundToContain(percentageSize.width, experience.get("width")),
                height: MathUtil.roundToContain(percentageSize.height, experience.get("height"))
            });
        },

        /**
         * Set the widget position after fit. If the widget is larger than
         * the experience it has move back to fit.
         *
         */
        _setFittedWidgetPosition: function() {
            // Initial positioning of fitted side
            if (this.get("width") === 100) {
                this.set({left: 0});
            }
            if (this.get("height") === 100) {
                this.set({top: 0});
            }

            // Out of the experience boundaries bottom check
            if ((this.get("height") + this.get("top")) > 100) {
                // Back widget to the boundaries of canvas
                this.set({top: 100 - this.get("height")});
            }

            // Out of the experience boundaries right check
            if ((this.get("width") + this.get("left")) > 100) {
                // Back widget to the boundaries of canvas
                this.set({left: 100 - this.get("width")});
            }
            // Out of the experience boundaries top check
            if (this.get("top") < 0) {
                // Back widget to the boundaries of canvas
                this.set({top: 0});
            }

            // Out of the experience boundaries left check
            if (this.get("left") < 0) {
                // Back widget to the boundaries of canvas
                this.set({left: 0});
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/AbstractWidgetEditorMixin.js